import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { ZeitstrahlReplay } from "@/components/zeitstrahl/ZeitstrahlReplay";
import { t } from "@/lib/i18n";
import type { IncidentData } from "@/lib/zeitstrahl/types";

/**
 * Störungs-Detail. Lädt die Störungsdaten vom Backend
 * (`GET /api/zeitstrahl/:id` → {@link IncidentData}, zusammengesetzt aus
 * Trace-Logs + Signalfenster + signal_map) und rendert das Replay. Liegt für
 * die Störung noch kein Signalfenster vor (404 — Blackbox hat keins geschickt),
 * wird das klar angezeigt statt eines Fake-Datensatzes.
 */
export function ZeitstrahlDetailPage() {
  const { incidentId = "" } = useParams();
  const [incident, setIncident] = useState<IncidentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await api.get<IncidentData>(`/api/zeitstrahl/${encodeURIComponent(incidentId)}`);
      setIncident(data);
    } catch (e) {
      if (e instanceof Error && e.message.startsWith("HTTP 404")) {
        setIncident(null);
        setNotFound(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Link to="/zeitstrahl" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <span className="font-mono text-sm">{incidentId}</span>
          </span>
        }
        description={t("Störungs-Replay — ein Cursor über alle Geräte und Signale.")}
        actions={
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            {t("Aktualisieren")}
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        {incident ? (
          <>
            <Card title={t("Störungs-Replay")} description={t("Signaldaten aus der Zelle (Blackbox).")}>
              <ZeitstrahlReplay data={incident} incidentId={incidentId} />
            </Card>

            <Card title={t("Beteiligte Geräte ({n})", { n: incident.devices.length })}>
              <ul className="flex flex-wrap gap-2">
                {incident.devices.map((d) => (
                  <li
                    key={d.id}
                    className="rounded-md border border-border bg-muted/30 px-2.5 py-1 font-mono text-xs"
                  >
                    {d.name}
                  </li>
                ))}
              </ul>
            </Card>
          </>
        ) : notFound ? (
          <Card title={t("Keine Signaldaten")}>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t(
                "Für diese Störung liegt noch kein Signalfenster vor. Blackbox schickt es pro Störfall: die Events als Logs mit gemeinsamer",
              )}{" "}
              <span className="font-mono">trace_id</span> {t("plus das Signalfenster via")}{" "}
              <span className="font-mono">PUT /api/zeitstrahl/&#123;id&#125;/window</span>.
            </p>
          </Card>
        ) : loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : null}
      </div>
    </div>
  );
}
